import { Component } from '@angular/core';
import { GenialLayoutService } from './genial-layout.service';
import { ImportContext, StatementImportService } from './statement-import.service';
import { ImportAsset, ImportDraft, ImportResult, NEW_RECORD, StatementPreview } from './statement-reader.model';

@Component({
  selector: 'app-statement-reader',
  standalone: false,
  templateUrl: './statement-reader.component.html',
  styleUrl: './statement-reader.component.scss'
})
export class StatementReaderComponent {
  readonly NEW_RECORD = NEW_RECORD;

  selectedFile: File | null = null;
  extractedText: string | null = null;
  preview: StatementPreview | null = null;
  errorMessage = '';
  isLoading = false;

  context: ImportContext | null = null;
  draft: ImportDraft | null = null;
  confirmDuplicate = false;
  isGenerating = false;
  result: ImportResult | null = null;

  constructor(
    private pdfReaderService: GenialLayoutService,
    private importService: StatementImportService
  ) { }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.item(0) ?? null;
    this.errorMessage = '';
    this.preview = null;
    this.extractedText = null;
    this.draft = null;
    this.result = null;

    if (file && (file.type !== 'application/pdf' || file.size > 25 * 1024 * 1024)) {
      this.selectedFile = null;
      this.errorMessage = 'Selecione um arquivo PDF de até 25 MB.';
      input.value = '';
      return;
    }

    this.selectedFile = file;
  }

  async processPDF(): Promise<void> {
    if (!this.selectedFile) {
      this.errorMessage = 'Selecione um arquivo PDF.';
      return;
    }

    this.errorMessage = '';
    this.draft = null;
    this.result = null;
    this.confirmDuplicate = false;
    this.isLoading = true;
    try {
      this.extractedText = await this.pdfReaderService.extractTextFromPDF(this.selectedFile);
      this.preview = this.pdfReaderService.parse(this.extractedText);
      if (!this.preview.trades.length) {
        this.errorMessage = 'Nenhum negócio encontrado. Verifique se é uma nota de corretagem da Genial.';
        return;
      }

      const [context, alreadyImported] = await Promise.all([
        this.importService.loadContext(),
        this.importService.countImported(this.preview.noteNumber)
      ]);
      this.context = context;
      this.draft = this.importService.buildDraft(this.preview, context, alreadyImported);

    } catch (error) {
      console.error('Erro ao processar nota:', error);
      this.errorMessage = 'Erro ao processar o arquivo.';
    } finally {
      this.isLoading = false;
    }
  }

  // An existing share defines where its operations go (Stocks or Reits)
  onShareSelected(asset: ImportAsset): void {
    const share = this.context?.shares.find(s => s.code == asset.shareCode);
    if (share) asset.type = share.type as ImportAsset['type'];
    this.onTypeChanged();
  }

  onTypeChanged(): void {
    if (this.draft) this.importService.applyTaxes(this.draft);
  }

  // Suggest the company code from the ticker root, e.g. EZTC3 -> EZTC
  onNewShareCode(asset: ImportAsset, code: string): void {
    const previousRoot = tickerRoot(asset.newShare.code);
    asset.newShare.code = code.toUpperCase();
    if (!asset.newCompany.code || asset.newCompany.code == previousRoot)
      asset.newCompany.code = tickerRoot(asset.newShare.code);
  }

  get validationErrors(): string[] {
    return this.draft && this.context ? this.importService.validate(this.draft, this.context) : [];
  }

  get canGenerate(): boolean {
    return !!this.draft
      && !this.isGenerating
      && !this.result
      && (!this.draft.alreadyImported || this.confirmDuplicate)
      && this.validationErrors.length == 0;
  }

  async generate(): Promise<void> {
    if (!this.draft || !this.context || !this.canGenerate) return;

    this.isGenerating = true;
    this.errorMessage = '';
    try {
      this.result = await this.importService.commit(this.draft, this.context);
    } catch (error) {
      console.error('Erro ao gerar lançamentos:', error);
      this.errorMessage = 'Erro ao gerar os lançamentos. Confira o que já foi criado antes de tentar de novo.';
    } finally {
      this.isGenerating = false;
    }
  }
}

function tickerRoot(code: string): string {
  return code.replace(/\d+[A-Z]?$/, '');
}
